import { ConfigProvider, Tabs, theme, ThemeConfig, Typography } from 'antd';
import clsx from 'clsx';
import Cookies from 'js-cookie';
import { debounce } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import Banner from '@/components/Global/Banner';

import Books from './Books';
import Courses from './Courses';
import Schools from './Schools';
import { setBookToken } from '../shared/getToken';
import { hasNewBookAtom } from '../../atoms/hasNewBook';
import { user } from '../../atoms/user';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import {
  getallbookslistApi,
  getBookToken,
  getSyncBookListApi,
  updateHasNewProduct,
} from '../../services/Library/Books';
import { BookInteraction } from '../../utils/interfaces/Library/Books/MyBook';
import { User } from '../../utils/interfaces/User';

const config: ThemeConfig = {
  token: { colorPrimary: 'white', fontFamily: 'inherit' },
};

const Library = () => {
  const activeTabRef = useRef<string>('1');
  const previousTabRef = useRef<string | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const userData: User | undefined = useRecoilValue(user);
  const [myBookList, setMyBookList] = useState<BookInteraction[]>();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [, setHasNewBook] = useRecoilState<boolean>(hasNewBookAtom);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const { search: keyWord, tab } = router.query;

  useEffect(() => {
    if (tab === '1' || tab === undefined) {
      setSearchKeyword((keyWord as string) || '');
    }
  }, [keyWord, tab]);

  const fetchBooks = useCallback(async (title?: string) => {
    setIsLoading(true);
    try {
      const response = await getallbookslistApi(title);
      setMyBookList(response?.data);
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getMyBookListWithSync = async () => {
    try {
      setIsLoading(true);

      await getSyncBookListApi();

      const response = await getallbookslistApi();
      setMyBookList(response?.data);
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = useCallback(
    debounce((keyword: string) => {
      fetchBooks(keyword);
    }, 500),
    [fetchBooks],
  );

  useEffect(() => {
    if (searchKeyword) {
      handleSearch(searchKeyword);
    } else {
      fetchBooks();
    }
    return () => handleSearch.cancel();
  }, [searchKeyword, fetchBooks]);

  const handleGetBookToken = async () => {
    if (userData) {
      try {
        const response = await getBookToken();
        await setBookToken(response?.data?.file_token);
      } catch (error) {
        console.error('API call error:', error);
      }
    }
  };

  useEffect(() => {
    const fetchBookToken = async () => {
      try {
        if (userData) {
          handleGetBookToken();
          handleHasVisitedLibrary();
        }
      } catch (error) {
        console.error('Error fetching book token:', error);
      }
    };

    fetchBookToken();
  }, [userData]);

  useEffect(() => {
    if (tab) {
      activeTabRef.current = tab as string;
    }
  }, [tab]);

  useEffect(() => {
    const currentTime = new Date();

    if (startTimeRef.current && previousTabRef.current) {
      triggerSessionEvent(
        previousTabRef.current,
        startTimeRef.current,
        currentTime,
      );
    }

    triggerViewEvent(activeTabRef.current);
    previousTabRef.current = activeTabRef.current;
    startTimeRef.current = currentTime;
  }, [tab]);

  const handleHasVisitedLibrary = async () => {
    try {
      await updateHasNewProduct({ hasNewDigitalProduct: false });
      Cookies.set('hasNewBookInLibrary', 'false', {
        expires: 1,
      });
      setHasNewBook(false);
    } catch (error: any) {
      console.error('Error logging event:', error);
    }
  };

  const tabs = [
    {
      key: '1',
      label: (
        <Typography.Title
          level={2}
          className={clsx(
            (tab === '1' || !tab) && 'text-white',
            'text-sm font-medium  px-1',
          )}
        >
          Books
        </Typography.Title>
      ),
      children: (
        <Books
          getMyBookListWithSync={getMyBookListWithSync}
          myBookList={myBookList}
          isLoading={isLoading}
        />
      ),
    },
    {
      key: '2',
      label: (
        <Typography.Title
          level={2}
          className={clsx(
            tab === '2' && 'text-white',
            'text-sm font-medium  px-1',
          )}
        >
          Schools
        </Typography.Title>
      ),
      children: <Schools />,
    },
    {
      key: '3',
      label: (
        <Typography.Title
          level={2}
          className={clsx(
            tab === '3' && 'text-white',
            'text-sm font-medium  px-1',
          )}
        >
          Courses
        </Typography.Title>
      ),
      children: <Courses />,
    },
  ];

  const handleTabChange = (key: string) => {
    activeTabRef.current = key;
    router.push({ pathname: router.pathname, query: { tab: key } }, undefined, {
      shallow: true,
    });
  };

  const triggerViewEvent = (tabKey: string) => {
    const eventParams: EventDetails = {
      event_name: 'screen_view',
      source: tabKey === '3' ? 'courses' : tabKey === '2' ? 'schools' : 'books',
      source_type: 'library',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const triggerSessionEvent = (
    tabKey: string | null,
    start: Date,
    end: Date,
  ) => {
    if (!tabKey) return;
    const sessionDuration = (end.getTime() - start.getTime()) / 1000;

    const eventParams: EventDetails = {
      event_name: 'screen_session',
      source: tabKey === '3' ? 'courses' : tabKey === '2' ? 'schools' : 'books',
      source_type: 'library',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'seconds',
      value: sessionDuration,
      activity_start: moment(start).format('YYYY-MM-DD HH:mm:ss'),
      activity_end: moment(end).format('YYYY-MM-DD HH:mm:ss'),
    };
    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
    startTimeRef.current = null;
  };

  useEffect(() => {
    return () => {
      const currentTime = new Date();
      if (startTimeRef.current) {
        triggerSessionEvent(
          previousTabRef.current,
          startTimeRef.current,
          currentTime,
        );
      }
    };
  }, []);

  return (
    <ConfigProvider theme={{ ...config, algorithm: [theme.darkAlgorithm] }}>
      <div className='relative overflow-hidden'>
        <div className=' pt-10 pb-10 xl:pb-20 px-5 md:px-12'>
          <Banner className='mb-3' />
          <Tabs
            activeKey={activeTabRef.current}
            items={tabs}
            onTabClick={handleTabChange}
            className='customTabProfile  customTabs'
            tabBarExtraContent={
              <style>{`.ant-tabs-ink-bar { background-color: white !important; }`}</style>
            }
          />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Library;
